/**
 * CurrentStatusCard Component
 * 
 * Card component that displays the current sound file waveform based on
 * the selected date from the chart click interaction.
 */

import React, { useState, useEffect } from 'react';
import Card from '@amzn/meridian/card';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Loader from '@amzn/meridian/loader';
import { useUltsSoundFiles, useWavFileUrl } from '../../../hooks';
import { WavFilePlayer } from './wav-file-player';

export interface CurrentStatusCardProps {
  /** Site identifier */
  site: string;
  /** Asset identifier */
  asset: string;
  /** Position identifier (may include equipment prefix) */
  position: string;
  /** Selected date from chart click (YYYY-MM-DD format) */
  selectedDate?: string;
}

/**
 * CurrentStatusCard component that displays waveform for the selected date
 */
export const CurrentStatusCard: React.FC<CurrentStatusCardProps> = ({
  site,
  asset,
  position,
  selectedDate
}) => {
  const { data: soundFiles, isLoading: isLoadingSoundFiles, error: soundFilesError } = useUltsSoundFiles({
    site,
    asset,
    position
  });
  
  const { url: wavFileUrl, isLoading: isLoadingWavUrl, error: wavUrlError, generateUrl, clear } = useWavFileUrl();
  
  const [currentSoundFile, setCurrentSoundFile] = useState<any>(null);

  // Find matching sound file for selected date
  useEffect(() => {
    if (soundFiles?.soundFiles && selectedDate) {
      const matchingFile = soundFiles.soundFiles.find(file => file.date === selectedDate);
      if (matchingFile && matchingFile.fileKey !== currentSoundFile?.fileKey) {
        setCurrentSoundFile(matchingFile);
        clear(); // Clear previous URL
        generateUrl(site, matchingFile.fileKey);
      } else if (!matchingFile) {
        setCurrentSoundFile(null);
        clear();
      }
    }
  }, [soundFiles, selectedDate, site, generateUrl, clear, currentSoundFile?.fileKey]);

  // Loading state
  if (isLoadingSoundFiles) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Current Spectrogram/Waveform Status</Text>
          <Row spacing="200">
            <Loader size="small" type="circular" />
            <Text type="b200">Loading sound files...</Text>
          </Row>
        </Column>
      </Card>
    );
  }

  // Error state
  if (soundFilesError) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Current Spectrogram/Waveform Status</Text>
          <Alert type="error">
            <Text type="b200">{soundFilesError}</Text>
          </Alert>
        </Column>
      </Card>
    );
  }

  // No sound files available
  if (!soundFiles?.soundFiles || soundFiles.soundFiles.length === 0) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Current Spectrogram/Waveform Status</Text>
          <Alert type="neutral">
            <Text type="b200">No sound files available for this position</Text>
          </Alert>
        </Column>
      </Card>
    );
  }

  // Empty state when no date is selected
  if (!selectedDate) {
    return (
      <Card>
        <Column spacing="200">
          <Text type="h100">Current Spectrogram/Waveform Status</Text>
          <Alert type="neutral">
            <Text type="b200">Click a chart point to view sound file</Text>
          </Alert>
        </Column>
      </Card>
    );
  }

  // No matching sound file for selected date
  if (selectedDate && !currentSoundFile) {
    return (
      <Card>
        <Column spacing="300">
          <Text type="h100">Current Spectrogram/Waveform Status</Text>
          <Alert type="neutral">
            <Text type="b200">No sound file available for date: {selectedDate}</Text>
          </Alert>
        </Column>
      </Card>
    );
  }

  // Display current sound file
  return (
    <Card>
      <Column spacing="300">
        <Text type="h100">Current Spectrogram/Waveform Status</Text>
        
        {currentSoundFile && (
          <Row spacing="200">
            <Row spacing="100">
              <Text type="b300">Date:</Text>
              <Text type="b200">{currentSoundFile.date}</Text>
            </Row>
            <Row spacing="100">
              <Text type="b300">Value:</Text>
              <Text type="b200">{currentSoundFile.val} dB</Text>
            </Row>
          </Row>
        )}

        {isLoadingWavUrl && (
          <Column spacing="200">
            <Loader size="medium" />
            <Text type="b300">Loading waveform...</Text>
          </Column>
        )}

        {wavUrlError && (
          <Alert type="error">
            <Text type="b200">Failed to load sound file: {wavUrlError}</Text>
          </Alert>
        )}
        
        {wavFileUrl && (
          <WavFilePlayer 
            wavFileUrl={wavFileUrl}
            onError={(error) => {
              console.error('Current Spectrogram/Waveform Status WAV file player error:', error);
            }}
            onReady={() => {
              console.log('Current Spectrogram/Waveform Status WAV file player ready');
            }}
          />
        )}
      </Column>
    </Card>
  );
};
